import React from 'react';
import { Toaster } from 'sonner';
import { LogoMark } from './Logo';

// The brand hexagon, shown as the icon on every toast (Rob's ask).
const brandIcon = <LogoMark size={22} />;

// HolidayHive's toasts, wearing the brand: top-centre, the hexagon mark, Poppins
// titles + Nunito Sans body, a warm rounded card with the soft "hive" shadow,
// and a green/red/amber cue so success vs error still reads at a glance. The
// actual look lives in index.css (targets the hh-toast-* classNames below plus
// sonner's [data-type] attribute); this component just wires the structure.
//
// Drop-in replacement for <Toaster/> — mounted once per page like before.
//
// ⚠️ IT IS ACTUALLY MOUNTED TWICE ON EVERY PAGE, AND THAT IS HARMLESS. MEASURED
// 10/09/2026, IN THE RUNNING APP — DON'T RE-DIAGNOSE IT FROM SCRATCH.
//
// `App.js` mounts one inside AuthProvider (so it wraps every route), and
// twenty-two pages ALSO mount their own. Sonner has no singleton guard: each
// <Toaster/> subscribes to the shared toast store independently, so every toast
// is rendered twice.
//
// The reason nobody has ever seen a doubled toast is that both containers are
// position="top-center" with the same offset, so the duplicate lands PIXEL-
// PERFECTLY on top of the original. Counted in the DOM on /payroll:
//
//     toaster containers : 2
//     toast elements     : 2 for one message (1 per container), both at top:16px
//
// ⚠️ **DO NOT BLAME THIS FOR THE NEXT TOAST ODDITY WITHOUT MEASURING FIRST.**
// It is exactly the sort of wrong-looking thing that invites a confident wrong
// diagnosis: on 10/09/2026 it was blamed for a persistent toast (duration:
// Infinity) not sticking, and for a phantom "six cards from two messages"
// stacking bug. BOTH WERE WRONG. The six cards were three real toasts, one left
// over from a previous test, each duplicated — plain duplication, no stacking
// fault. And both copies share a duration and dismiss on the same id, so the
// duplication cannot explain a persistence problem either.
//
// Left alone deliberately (Rob's call, 10/09/2026): removing twenty-two mounts
// touches twenty-two files, risks silencing a sign-up page that might sit
// outside the App.js tree, and fixes nothing anybody can see. If you are here
// because toasts ARE misbehaving, count the containers in the DOM first —
// `document.querySelectorAll('[data-sonner-toaster]')` — and confirm the
// duplication is genuinely implicated before touching it.
const BrandedToaster = (props) => (
  <Toaster
    position="top-center"
    offset={16}
    gap={10}
    /*
      ⚠️ expand, ALWAYS. Sonner's default collapses a second toast into a "peek" behind
      the first with its text hidden until hover — which, wearing our card styling,
      renders as a blank white slab that looks broken rather than stacked. Rob hit it
      signing in: "Welcome back" plus the 2FA reminder arrived together and the second
      was an empty box (18/08/2026). Expanded, every toast shows its words.
    */
    expand
    closeButton
    icons={{ success: brandIcon, error: brandIcon, info: brandIcon, warning: brandIcon, loading: brandIcon }}
    toastOptions={{
      classNames: {
        toast: 'hh-toast',
        title: 'hh-toast-title',
        description: 'hh-toast-desc',
        icon: 'hh-toast-icon',
        closeButton: 'hh-toast-close',
      },
    }}
    {...props}
  />
);

export default BrandedToaster;
